import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { getUserById, updateUser } from '@/services/userService';
import { getRoles } from '@/services/roleService';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import styles from './UserModals.module.scss';

// ============================================
// TYPES
// ============================================

interface AssignRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
}

// ============================================
// COMPONENT
// ============================================

function AssignRolesModal({ isOpen, onClose, userId }: AssignRolesModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Fetch user detail
  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId!),
    enabled: isOpen && !!userId,
  });

  // Fetch available roles
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['roles', 'pickerAll'],
    queryFn: () => getRoles({ page: 0, size: 100 }),
    enabled: isOpen,
  });

  // Either query failing means the modal can't perform its purpose — pre-fill
  // needs the user, the picker needs the roles list. Show inline Forbidden;
  // the Save button is also gated below so a 403 can't be retried via submit.
  const isForbidden = isForbiddenError(userError) || isForbiddenError(rolesError);

  const roleOptions = useMemo(
    () =>
      (rolesData?.content ?? []).map((role) => ({
        label: role.name,
        value: role.id,
      })),
    [rolesData],
  );

  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  // Pre-fill from user data
  useEffect(() => {
    if (user) {
      setSelectedRoleIds(user.roles.map((r) => r.id));
    }
  }, [user]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedRoleIds([]);
    }
  }, [isOpen]);

  const updateMutation = useMutation({
    mutationFn: () => updateUser(userId!, { roleIds: selectedRoleIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['user', userId] });
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const handleSave = useCallback(() => {
    updateMutation.mutate();
  }, [updateMutation]);

  const handleClose = useCallback(() => {
    if (!updateMutation.isPending) {
      onClose();
    }
  }, [updateMutation.isPending, onClose]);

  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        userName
          ? t('K_MODAL_ASSIGN_ROLES_FOR', 'Assign Roles — {name}').replace('{name}', userName)
          : t('K_MODAL_ASSIGN_ROLES', 'Assign Roles')
      }
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={updateMutation.isPending}
          >
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending || userLoading || isForbidden}
            loading={updateMutation.isPending}
          >
            {t('K_SAVE', 'Save')}
          </Button>
        </div>
      }
    >
      {userLoading ? (
        <p>{t('K_LOADING', 'Loading...')}</p>
      ) : isForbidden ? (
        <Forbidden variant="inline" hideAction />
      ) : (
        <div className={styles.form}>
          <MultiSelect
            label={t('K_LABEL_ROLES', 'Roles')}
            placeholder={t('K_PLACEHOLDER_SELECT_ROLES', 'Select roles')}
            options={roleOptions}
            value={selectedRoleIds}
            onChange={setSelectedRoleIds}
            loading={rolesLoading}
            searchable
          />
        </div>
      )}
    </Modal>
  );
}

export default AssignRolesModal;
